import { useState, memo, useMemo, useContext } from 'react';
import { AccessibleDivButton, FavoriteIcon } from '@/components';
import { cx } from '@emotion/css';
import { Skeleton, Stack } from '@mui/material';
import removedFlyerCardIcon from '@/assets/removed_flyer_card.svg';
import { FlyerType } from '@/config/constants';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useCategories } from '@/hooks';
import { FlyerCardThumbnail, TitleTranslations } from '@/graphql/types';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import styles from './Card.styles';
import {
  BodyLargeEmphasized,
  BodyMedium,
  BodySmall,
  CommunityTag,
  ExpiredTag,
  ExtendedCommunityTag,
  SchoolTag,
  Tag,
} from '@/design-system';
import colors from '@/theme/colors';
import { districtVar } from '@/graphql/state';
import { useReactiveVar } from '@apollo/client';
import { useNavigateWithTracking } from '@/hooks';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import UserPreferencesContext from '@/context/UserPreferencesContext';

interface SavedFlyerCardProps {
  trackId: string;
  flyer: FlyerCardThumbnail;
  isExpired?: boolean;
  isRemoved?: boolean;
  unsaveFlyer: (flyerId: number) => void;
  dataTestId?: string;
  flyerType: string;
  isExpandedCommunity?: boolean;
  titleTranslations?: TitleTranslations;
  isCommunityFree?: boolean;
}

const CardPure: React.FC<SavedFlyerCardProps> = ({
  trackId,
  flyer,
  isExpired = false,
  isRemoved = false,
  unsaveFlyer,
  flyerType,
  isExpandedCommunity = false,
  dataTestId,
  titleTranslations,
  isCommunityFree,
}) => {
  const district = useReactiveVar(districtVar);
  const navigateWithTracking = useNavigateWithTracking();
  const { language } = useContext(UserPreferencesContext);
  const { track } = useAnalytics();
  const { schoolCategories, communityCategories } = useCategories();
  const translatedTitle = titleTranslations ? titleTranslations[language.code] : undefined;
  const flyerTitle = translatedTitle ?? flyer.title;
  const [loadingFlyer, setLoadingFlyer] = useState(true);
  const onLoadFlyer = () => {
    setLoadingFlyer(false);
  };

  const disableFlyer = isExpired || isRemoved;

  const flyerCategories = useMemo(
    () =>
      flyer.type === FlyerType.school
        ? schoolCategories.filter(category => flyer.categories.includes(category.id))
        : communityCategories.filter(category => flyer.categories.includes(category.id)),
    [flyer.categories, schoolCategories, communityCategories]
  );

  const gradesOrAges = useMemo(() => {
    const { type, minTargetGradeOrAge, maxTargetGradeOrAge } = flyer;
    if (minTargetGradeOrAge && maxTargetGradeOrAge) {
      const prefix = type === FlyerType.community ? 'AGE' : 'GR';
      let gradeOrAge = '';
      if (minTargetGradeOrAge === maxTargetGradeOrAge) {
        gradeOrAge = minTargetGradeOrAge;
      } else {
        gradeOrAge = `${minTargetGradeOrAge}-${maxTargetGradeOrAge}`;
      }
      return `${prefix} ${gradeOrAge}`;
    }
    return null;
  }, [flyer]);

  const goToFlyerDetails = (flyerId: number, flyerTitle: string) => {
    if (isRemoved) return;
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:go_to_flyer_details`,
      label: flyerTitle,
      value: flyerId,
    });
    navigateWithTracking(`/district/${district?.districtId}/flyer/${flyerId}`, {
      event_from: TrackingEvents.viewSavedFlyers,
      event_detail_from: TrackingEvents.viewSavedFlyers,
      stage_from: TrackingStage.consideration,
    });
  };

  const handleUnsaveFlyer = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:unfavorite_flyer`,
      label: flyer.title,
      value: Number(flyer.flyerId),
    });
    unsaveFlyer(flyer.flyerId);
  };

  const FlyerTag = () => {
    if (flyerType == FlyerType.school) return <SchoolTag />;

    return (
      <div className={styles.communityTag}>
        {isExpandedCommunity ? <ExtendedCommunityTag /> : <CommunityTag />}
        {isCommunityFree && <Tag variant="free">Free</Tag>}
      </div>
    );
  };

  return (
    <div className={styles.wrapper} data-testid="SavedFlyerCard">
      {loadingFlyer && (
        <div className={styles.loader}>
          <Stack spacing={1}>
            <div className={styles.container}>
              <div>
                <Skeleton variant="rectangular" width="125px" height="158px" />
              </div>
              <div className={styles.skeletonContent}>
                <Skeleton variant="text" height="30px" />
                <Skeleton variant="text" height="30px" />
                <Skeleton variant="text" height="30px" />
              </div>
            </div>
          </Stack>
        </div>
      )}

      <AccessibleDivButton
        className={cx(styles.container, disableFlyer ? styles.disabledFlyer : {})}
        style={loadingFlyer ? { display: 'none' } : {}}
        data-testid={dataTestId}
        key={flyer.flyerId}
        onClick={() => goToFlyerDetails(flyer.flyerId, flyer.title)}
        //  data-testid="savedFlyerContent"
        role="link"
      >
        <div className={styles.content}>
          <div className={cx(styles.flyer, disableFlyer && styles.disabledMedia)}>
            <img
              onLoad={onLoadFlyer}
              src={isRemoved ? removedFlyerCardIcon : flyer.frontImageUrl}
              alt={flyerTitle}
              width="125px"
              height="158px"
              data-testid="flyerThumbnail"
            />
          </div>
          <div className={styles.body}>
            <div className={styles.titleRow}>
              <BodyLargeEmphasized
                className={styles.title}
                nonTranslate
                data-testid="flyerTitle"
                color={disableFlyer ? colors.slate : colors.prussian}
              >
                {flyerTitle}
              </BodyLargeEmphasized>
              {!loadingFlyer && (
                <AccessibleDivButton
                  onClick={handleUnsaveFlyer}
                  aria-label="Unsave favorite flyer button"
                  className={styles.favoriteIcon}
                  data-testid="unsaveFlyerButton"
                >
                  <FavoriteIcon size={20} />
                </AccessibleDivButton>
              )}
            </div>
            {isRemoved ? (
              <div className={styles.removedFlyerLabel}>
                <BodyMedium>This flyer is removed.</BodyMedium>
              </div>
            ) : (
              <>
                {flyerCategories.map((category, categoryIndex) => (
                  <BodySmall color={colors.slate} data-testid="category" key={category.id}>
                    {category.title}
                    {categoryIndex < flyerCategories.length - 1 ? ' • ' : ''}
                  </BodySmall>
                ))}
                <div className={styles.agesOrGradesContainer}>
                  <FlyerTag />
                  {gradesOrAges && <Tag variant="greyOutline">{gradesOrAges}</Tag>}
                  {isExpired && <ExpiredTag />}
                </div>
              </>
            )}
          </div>
        </div>
      </AccessibleDivButton>
    </div>
  );
};

export const Card = memo(CardPure);
